import { z } from 'zod'
import { remapLegacySarIndicators } from '@edge/charts-indicators'
import {
  ColorRuleSchema,
  ColumnDisplayModesSchema,
} from '@/features/sliced-form/schemas/profile-presets'
import { audioSettingsSchema } from '@/features/scanner/alerts/settings/audio-settings-types'
import { alertLogShowSchema } from '@/features/scanner/alerts/alert-log-show'
import { newsAudioSettingsSchema } from '@/features/news/audio/news-audio-settings'
import type { PanelState } from '@/workspace/types'
import { NotebookPanelStateSchema } from '@/features/notebook/schemas/note-types'
import { AlertSpecSchema } from '@/features/alert-windows/alert-window-types'

/** Accepts legacy 'fitToHeaders' and migrates it to 'fitToData'. */
const ColumnSizeModeSchema = z
  .enum(['fitToData', 'fitToHeaders', 'fitToPanel', 'manual'])
  .transform(
    (v) => (v === 'fitToHeaders' ? 'fitToData' : v) as 'fitToData' | 'fitToPanel' | 'manual',
  )

/**
 * Persisted grid sort. An ordered list of the currently-sorted columns
 * (`sortIndex` preserves multi-sort order). Captured on every user sort and
 * re-applied on grid mount so a sort survives a layout-tab remount — otherwise
 * AG Grid rebuilds from `columnDefs` and the column's default sort reasserts.
 *
 *   - `undefined` → never sorted; the colDef's default sort applies.
 *   - `[]`        → user explicitly cleared all sorts (default is suppressed).
 */
export const ColumnSortStateSchema = z.array(
  z.object({
    colId: z.string(),
    sort: z.enum(['asc', 'desc']),
    sortIndex: z.number().nullable().optional(),
  }),
)

export type ColumnSortState = z.infer<typeof ColumnSortStateSchema>

/**
 * Saved-profile links, keyed by the panel-state key that holds the profile id
 * (`filterProfileId`, `columnProfileId`, ...). `fp` fingerprints the profile
 * contents the panel's copy was taken from, so saving that profile can update
 * this panel unless the user has tweaked it. See
 * `features/sliced-form/profile-link/profile-link.ts`.
 */
export const ProfileLinksSchema = z.record(z.object({ id: z.string(), fp: z.string() }))

export type ProfileLinks = z.infer<typeof ProfileLinksSchema>

// ─── Chart Panel State Schema ───

/**
 * Validates chart-specific panel state.
 * Uses loose validation (passthrough + z.unknown()) because the @edge/charts
 * types (Period, IndicatorEntry, ChartSettings) are complex external types
 * that shouldn't be tightly coupled to persistence validation.
 */
export const ChartPanelStateSchema = z
  .object({
    period: z.record(z.unknown()).optional(),
    chartType: z.string().optional(),
    mainIndicators: z.array(z.unknown()).optional(),
    subIndicators: z.array(z.unknown()).optional(),
    settings: z.record(z.unknown()).optional(),
    drawingBarVisible: z.boolean().optional(),
    // Drawing-bar eye: hide-all-drawings / hide-all-indicators. Explicit (not
    // just passthrough) so the persistence contract survives a future .strip().
    drawingsHidden: z.boolean().optional(),
    indicatorsHidden: z.boolean().optional(),
    // Indicator-legend minimize chevron — persisted so the collapsed state
    // survives a refresh (IDB + API path, same as drawingsHidden/indicatorsHidden).
    legendCollapsed: z.boolean().optional(),
    // Panel-local focus date (YYYY-MM-DD); independent of the workspace-shared
    // broadcast. Cleared by applyDateBroadcast on scanner row click.
    focusDate: z.string().nullable().optional(),
    // EdgeScript indicators pinned to the chart (dev-gated). Loose on purpose, like
    // the catalog indicator arrays: the entry schema lives in schemas/edgescript.ts and
    // the host validates each entry when it mounts.
    scriptIndicators: z.array(z.unknown()).optional(),
    // Financial metric sub-panes (Research > Chart > Graphs). Loose for the same
    // reason; the entry schema lives in schemas/financial-series.ts.
    financialSeries: z.array(z.unknown()).optional(),
    // The docked EdgeScript editor under this chart (dev-gated): open state plus which
    // pinned entry it edits, or the draft key of a script not yet added.
    scriptEditor: z
      .object({
        open: z.boolean(),
        entryId: z.string().optional(),
        draftKey: z.string().optional(),
        // What the dock is authoring: an indicator script (the default when an
        // entry is open) or a backtest strategy (chart-dock strategies spec,
        // 2026-09-06). Absent = the indicator editor (the header decides the kind).
        kind: z.enum(['indicator', 'strategy']).optional(),
        // Strategy: the saved strategy row once it exists, and its one editor.
        strategyId: z.string().optional(),
        editorMode: z.enum(['form', 'code']).optional(),
        // A preview run requested from outside the dock (the companion); any new
        // value runs once the strategy compiles clean.
        runRequested: z.number().optional(),
      })
      .optional(),
    // The dock's height in pixels, dragged on its top edge. A sibling of
    // `scriptEditor` rather than a field of it because open/locate/new all
    // rewrite that object whole, and the height should survive them.
    scriptEditorHeight: z.number().optional(),
  })
  .passthrough()

export type ChartPanelState = z.infer<typeof ChartPanelStateSchema>

// ─── Multi-Chart Panel State Schema ───

const GridDimSchema = z.union([z.literal(1), z.literal(2), z.literal(3)])

// Schema only — the canonical in-memory type is the hand-written interface in
// `features/multi-chart/multi-chart-state.ts`, which covers the richer set of
// fields the panel writes (filters, orderColumn, syncSettings, ...). The
// schema below stays loose (`.passthrough()`) so persisted state survives any
// field the validation table doesn't enumerate.
export const MultiChartPanelStateSchema = z
  .object({
    scanName: z.string().optional(),
    layout: z
      .object({
        rows: GridDimSchema,
        cols: GridDimSchema,
      })
      .optional(),
    period: z.record(z.unknown()).optional(),
    chartType: z.string().optional(),
    mainIndicators: z.array(z.unknown()).optional(),
    subIndicators: z.array(z.unknown()).optional(),
    settings: z.record(z.unknown()).optional(),
    // Persisted indicator-legend collapse state — shared across all cells in the
    // scanner (see ChartPanelStateSchema for the per-panel variant).
    legendCollapsed: z.boolean().optional(),
    // Auto-refresh timer (RefreshTimerControl): interval + paused, per panel.
    refreshIntervalMs: z.number().optional(),
    refreshPaused: z.boolean().optional(),
    // Temporarily excluded tickers, same persistence + expiry semantics as
    // the Scanner panel's fields below (shared via useExcludedTickers).
    excludedTickers: z.array(z.string()).optional(),
    excludedTickersExpiration: z.number().nullable().optional(),
  })
  .passthrough()

// ─── Scanner Panel State Schema ───

export const ScannerPanelStateSchema = z
  .object({
    selectedColumns: z.array(z.string()).optional(),
    filters: z.union([z.record(z.unknown()), z.array(z.unknown())]).optional(),
    colorRules: z.array(ColorRuleSchema).optional(),
    columnDisplayModes: ColumnDisplayModesSchema.optional(),
    filterProfileId: z.string().nullable().optional(),
    columnProfileId: z.string().nullable().optional(),
    scanName: z.string().optional(),
    excludedTickers: z.array(z.string()).optional(),
    excludedTickersExpiration: z.number().nullable().optional(),
    // Alert-log audio prefs (sound on/off, TTS, volume, cooldown, limits).
    // Persisted so a disabled Sound toggle survives a refresh.
    audioSettings: audioSettingsSchema.optional(),
    // Alert Log strip: persisted open/collapsed state and drag-resized height
    // (px), so both survive a refresh.
    alertLogOpen: z.boolean().optional(),
    alertLogHeight: z.number().optional(),
    // Alert Log "Show in log" choice (All / Added / Removed) from the Alert Log
    // settings popover. Absent means All.
    alertLogShow: alertLogShowSchema.optional(),
    columnWidths: z.record(z.number()).optional(),
    columnSizeMode: ColumnSizeModeSchema.optional(),
    // Panel-wide: column titles wrap onto at most two lines (column card).
    // Absent means ON; only switching it off stores a value (false).
    columnWrapHeaders: z.boolean().optional(),
    columnSort: ColumnSortStateSchema.optional(),
    // Sibling of `columnSort`: the colId whose sort is by ABSOLUTE value
    // (biggest movers regardless of sign), sent to the backend as
    // `order: 'ABS'`. It is the third step of the column header's click cycle
    // (asc → desc → |value| → unsorted); AG Grid column state only knows
    // asc/desc, so it cannot live inside ColumnSortState. Only honoured while
    // it names the column `columnSort` is actually sorting.
    columnSortMagnitude: z.string().optional(),
    // Panel-local focus date (YYYY-MM-DD). Scanner row click is the only
    // producer of the workspace-shared broadcast focusDate.
    focusDate: z.string().nullable().optional(),
    // Panel-local New York minute (HH:MM) of a minute snapshot, set next to a
    // past `focusDate` behind the scanner-minute-snapshot flag. Absent = the
    // whole-day history scan.
    focusTime: z.string().nullable().optional(),
    // Auto-refresh timer (RefreshTimerControl): interval + paused, per panel.
    refreshIntervalMs: z.number().optional(),
    refreshPaused: z.boolean().optional(),
    // Custom-RVOL "Advanced" tab config (query-shape; normalized at read time
    // via normalizeRvolBaselineConfig). Drives the frontend-computed
    // `custom_rvol` column against the user's chosen reference days. `filters`
    // is a QueryFilterGroup selecting the reference days; orderBy/order/limit
    // rank + cap them (see rvol-baseline.ts).
    rvolBaselineConfig: z
      .object({
        enabled: z.boolean().optional(),
        filters: z.record(z.unknown()).optional(),
        orderBy: z.string().optional(),
        order: z.enum(['ASC', 'DESC']).optional(),
        limit: z.number().optional(),
      })
      .passthrough()
      .optional(),
    // UNUSED since 2026-10-15: the Context tab now sets ONE global scanner
    // source (features/scanner/scanner-source-store.ts). Kept so saved layouts
    // carrying it still parse; nothing reads it.
    dataSource: z.enum(['database', 'realtime']).optional(),
  })
  .passthrough()

export type ScannerPanelState = z.infer<typeof ScannerPanelStateSchema>

// ─── Multi-Scanner Panel State Schema ───

const MultiScannerSelectedProfileSchema = z.object({
  profileId: z.string(),
  name: z.string(),
  filters: z
    .union([z.record(z.unknown()), z.array(z.unknown())])
    .nullable()
    .optional(),
  /** Watchlist section: the filter is derived from this id at query time. */
  watchlistId: z.number().optional(),
})

export const MultiScannerPanelStateSchema = z
  .object({
    selectedProfiles: z.array(MultiScannerSelectedProfileSchema).optional(),
    rowsPerGroup: z.union([z.literal(5), z.literal(10), z.literal(20)]).optional(),
    selectedColumns: z.array(z.string()).optional(),
    colorRules: z.array(ColorRuleSchema).optional(),
    columnProfileId: z.string().nullable().optional(),
    columnWidths: z.record(z.number()).optional(),
    columnSizeMode: ColumnSizeModeSchema.optional(),
    columnWrapHeaders: z.boolean().optional(),
    // Absolute-value sort on the primary sort column, as the scanner's
    // `columnSortMagnitude`: sent as `order: 'ABS'` and applied in the grid.
    columnSortMagnitude: z.string().optional(),
    columnSort: ColumnSortStateSchema.optional(),
  })
  .passthrough()

export type MultiScannerPanelState = z.infer<typeof MultiScannerPanelStateSchema>

// ─── Sector Heatmap Panel State Schema ───

export const SectorHeatmapPanelStateSchema = z
  .object({
    dimension: z.enum(['sector', 'industry', 'tag']).optional(),
    // Rendering: treemap (Grid), sunburst (Radial), or the Sector x bucket Matrix.
    // Default treemap. Matrix design: docs/superpowers/specs/2026-09-11-heatmap-matrix-design.md
    view: z.enum(['treemap', 'radial', 'matrix']).optional(),
    // Matrix column dimension (default capTier) and the bucket keys the user collapsed.
    matrixColumnBy: z.enum(['capTier', 'country', 'exchange', 'rank']).optional(),
    matrixHiddenBuckets: z.array(z.string()).optional(),
    // Chips shown per cell before the "+N" overflow chip (default 28).
    matrixMaxPerCell: z.number().optional(),
    // Color/size fields — a scanner column key, or 'breadth'/'equal'.
    metric: z.string().optional(),
    weight: z.string().optional(),
    // Universe filter (form shape) — set via the filter suite. Loosely typed
    // here; consumers resolve it. Absent → the base US-common-stock universe.
    filters: z.unknown().optional(),
    filterProfileId: z.string().nullable().optional(),
    scanName: z.string().optional(),
  })
  .passthrough()

export type SectorHeatmapPanelState = z.infer<typeof SectorHeatmapPanelStateSchema>

// ─── Theme Tracker Panel State Schema ───

/**
 * Theme Tracker panel (see docs/superpowers/plans/2026-08-18-theme-tracker-panel-plan.md).
 * L1 = roster of themes (rolled-up columns), L2 = drill into one theme's members.
 * The member grid mirrors the watchlist/scanner column pipeline
 * (`buildScannerGridColDefs`): column picker, per-column display modes, and color
 * rules, all persisted here.
 */
export const ThemeTrackerPanelStateSchema = z
  .object({
    // null = L1 roster, set = L2 drill into that theme. Persisted so a reload
    // restores the drilled view.
    activeThemeId: z.number().nullable().optional(),
    // Themes (kind='thematic', default) vs Sympathies (kind='sympathy', opt-in).
    // Drives the roster rollup query's `?kind=` param.
    themeKind: z.enum(['thematic', 'sympathy']).optional(),
    // Table (full grid) vs Bar (grid showing ONE field as an in-cell bar). ONE
    // shared mode for the whole panel (Chris, 2026-08-19): the roster (outside)
    // and the drill-in (inside) are always the same view — no per-level jumping.
    view: z.enum(['table', 'bar']).optional(),
    // In Bar view, the single field rendered as the bar — a base scanner key
    // SHARED across the roster (shows its `*_avg`) and members (raw value).
    barField: z.string().optional(),
    // Grid sort, stored PER LEVEL (the roster and member grids have different
    // columns, so a single shared `columnSort` would clobber across levels).
    rosterSort: ColumnSortStateSchema.optional(),
    memberSort: ColumnSortStateSchema.optional(),
    selectedColumns: z.array(z.string()).optional(),
    columnWidths: z.record(z.number()).optional(),
    columnSizeMode: ColumnSizeModeSchema.optional(),
    columnSort: ColumnSortStateSchema.optional(),
    columnProfileId: z.string().nullable().optional(),
    colorRules: z.array(z.record(z.unknown())).optional(),
    columnDisplayModes: ColumnDisplayModesSchema.optional(),
    // History mode: EOD data as-of this trading day (set by the header calendar).
    focusDate: z.string().nullable().optional(),
    // Auto-refresh timer (RefreshTimerControl): interval + paused, per panel.
    refreshIntervalMs: z.number().optional(),
    refreshPaused: z.boolean().optional(),
  })
  .passthrough()

export type ThemeTrackerPanelState = z.infer<typeof ThemeTrackerPanelStateSchema>

// ─── Script Editor Panel State Schema ───

/**
 * EdgeScript editor as a dashboard panel (phase 2 plan, Task 2.10; DEC-4). The same
 * editor the chart docks, without a chart: `scriptId` links the panel to a saved
 * My scripts entry, `draftKey` keys unsaved typing in `script-drafts.ts`.
 */
export const ScriptEditorPanelStateSchema = z
  .object({
    scriptId: z.string().optional(),
    draftKey: z.string().optional(),
  })
  .passthrough()

export type ScriptEditorPanelState = z.infer<typeof ScriptEditorPanelStateSchema>

// ─── History Heatmap Panel State Schema ───

/**
 * History-page category heatmap (Phase 1: single-level, no ticker leaves — see
 * docs/superpowers/specs/2026-08-10-history-analytics-panels-design.md). Driven
 * by the page-level history filters, not a per-panel universe, so — unlike
 * SectorHeatmapPanelStateSchema — there is no `filters`/`filterProfileId`.
 */
export const HistoryHeatmapPanelStateSchema = z
  .object({
    // Category dimension to group by. Mirrors chart-aggregate-sheet's
    // CATEGORY_OPTIONS (sector/industry/exchange/country/share_group).
    dimension: z.enum(['sector', 'industry', 'exchange', 'country', 'share_group']).optional(),
    // Rendering: treemap (Grid) or sunburst (Radial). Default treemap.
    view: z.enum(['treemap', 'radial']).optional(),
    // Color = a history column key averaged per category. Size = 'count' or a
    // history column key. Both default in the panel when absent.
    colorMetric: z.string().optional(),
    sizeMetric: z.string().optional(),
  })
  .passthrough()

export type HistoryHeatmapPanelState = z.infer<typeof HistoryHeatmapPanelStateSchema>

/**
 * History-page Time-of-Day Distribution panel (Phase 1) — see
 * docs/superpowers/specs/2026-08-10-history-analytics-panels-design.md
 * (Panel 2). Driven by the page-level history filters; no per-panel universe.
 */
export const HistoryTimeDistributionPanelStateSchema = z
  .object({
    // Which day's high/low time columns to distribute: the trigger day (day0)
    // or the day after (day1). Default day0.
    referenceDay: z.enum(['day0', 'day1']).optional(),
    // Which side(s) of the mirrored histogram to render. Default 'both'.
    extreme: z.enum(['both', 'hod', 'lod']).optional(),
    // Time-of-event field to distribute: session (all_day/regular/pm/ah) +
    // extreme (high/low), day-agnostic — `referenceDay` composes in the day
    // prefix at request time (see use-time-distribution-data.ts). The leaf's
    // high/low half is a display label only; both column families for the
    // session are always fetched (Extreme, above, is the actual display
    // filter). Default 'regular_high'.
    metric: z
      .enum([
        'all_day_high',
        'all_day_low',
        'regular_high',
        'regular_low',
        'pm_high',
        'pm_low',
        'ah_high',
        'ah_low',
      ])
      .optional(),
  })
  .passthrough()

export type HistoryTimeDistributionPanelState = z.infer<
  typeof HistoryTimeDistributionPanelStateSchema
>

// ─── Watchlist Panel State Schema ───

export const WatchlistPanelStateSchema = z
  .object({
    activeWatchlistId: z.number().nullable().optional(),
    // A curated Edge list by slug (layout presets): ids differ per environment.
    // A picked list's id wins over it (resolveActiveWatchlistId).
    activeWatchlistSlug: z.string().nullable().optional(),
    selectedColumns: z.array(z.string()).optional(),
    columnWidths: z.record(z.number()).optional(),
    columnSizeMode: ColumnSizeModeSchema.optional(),
    columnWrapHeaders: z.boolean().optional(),
    columnSort: ColumnSortStateSchema.optional(),
    columnProfileId: z.string().nullable().optional(),
    colorRules: z.array(z.record(z.unknown())).optional(),
    columnDisplayModes: ColumnDisplayModesSchema.optional(),
    // History mode: EOD data as-of this trading day (set by the header calendar).
    focusDate: z.string().nullable().optional(),
    // Auto-refresh timer (RefreshTimerControl): interval + paused, per panel.
    refreshIntervalMs: z.number().optional(),
    refreshPaused: z.boolean().optional(),
  })
  .passthrough()

export type WatchlistPanelState = z.infer<typeof WatchlistPanelStateSchema>

// ─── News Panel State Schema ───

const NewsCategoryStateSchema = z.object({
  flag: z.enum(['ALL', 'NONE', 'IN', 'NOT_IN']),
  data: z.array(z.string()).optional(),
})

export const NewsPanelStateSchema = z
  .object({
    newsType: z.enum(['news', 'sec']).optional(),
    maxArticles: z.number().optional(),
    /** LEGACY source toggle ('news'/'sec' array), superseded by `categories`;
     *  kept for read-time migration of persisted panels. */
    sources: z.array(z.enum(['news', 'sec'])).optional(),
    /** Serialized source categories (PRESS_RELEASE / OTHER_NEWS /
     *  SEC_CATEGORIES, each {flag, data?}): the /v2/news payload shape.
     *  See features/news/schemas/news-sources.ts. */
    categories: z
      .object({
        NEWS_SOURCES: NewsCategoryStateSchema.optional(),
        SEC_CATEGORIES: NewsCategoryStateSchema.optional(),
      })
      .passthrough()
      .optional(),
    selectedColumns: z.array(z.string()).optional(),
    filters: z.union([z.record(z.unknown()), z.array(z.unknown())]).optional(),
    filterProfileId: z.string().nullable().optional(),
    columnProfileId: z.string().nullable().optional(),
    columnWidths: z.record(z.number()).optional(),
    columnSizeMode: ColumnSizeModeSchema.optional(),
    columnSort: ColumnSortStateSchema.optional(),
    // Spoken-readout prefs. Optional so panels saved before the Audio tab
    // existed load unchanged; absent means silent.
    audioSettings: newsAudioSettingsSchema.optional(),
  })
  .passthrough()

export type NewsPanelState = z.infer<typeof NewsPanelStateSchema>

// ─── Generic Panel State Schema ───

/** Fallback schema for non-chart panels — accepts any object. */
const GenericPanelStateSchema = z.record(z.unknown())

// ─── History Panel State Schemas ───

export const HistoryRecordsPanelStateSchema = z
  .object({
    selectedColumns: z.array(z.string()).optional(),
    columnProfileId: z.string().nullable().optional(),
    columnDisplayModes: ColumnDisplayModesSchema.optional(),
    recordsOrderBy: z.string().optional(),
    recordsOrder: z.enum(['asc', 'desc']).optional(),
    gridColumnSizeKey: z.number().optional(),
    columnWidths: z.record(z.number()).optional(),
    columnSizeMode: ColumnSizeModeSchema.optional(),
    // Column-title wrap (column card Panel tab); absent means ON.
    columnWrapHeaders: z.boolean().optional(),
    columnSort: ColumnSortStateSchema.optional(),
  })
  .passthrough()

export const HistoryStatsPanelStateSchema = z
  .object({
    aggregateProfile: z.record(z.unknown()).optional(),
    hiddenLegendItems: z.array(z.string()).optional(),
    avgPriceAggregate: z.string().optional(),
    aggregateProfileId: z.string().nullable().optional(),
  })
  .passthrough()

export const HistoryComparisonPanelStateSchema = z
  .object({
    comparisonTicker: z.string().optional(),
    periods: z.array(z.record(z.unknown())).optional(),
    selectedColumns: z.array(z.string()).optional(),
    columnProfileId: z.string().nullable().optional(),
    columnWidths: z.record(z.number()).optional(),
    columnSizeMode: ColumnSizeModeSchema.optional(),
    // Column-title wrap (column card Panel tab); absent means ON.
    columnWrapHeaders: z.boolean().optional(),
    columnSort: ColumnSortStateSchema.optional(),
  })
  .passthrough()

export const HistoryChartPanelStateSchema = z
  .object({
    aggregateProfile: z.record(z.unknown()).optional(),
    // Named cohorts for the category chart (categoryColumn === '__cases__'):
    // profile-shape conditions per cohort, see CaseGroupDraft in stats-types.
    categoryCases: z
      .object({
        cases: z.array(z.object({ label: z.string(), conditions: z.array(z.record(z.unknown())) })),
        elseLabel: z.string().nullable().optional(),
      })
      .optional(),
    // Time-of-day bins for the category chart (categoryColumn === '__time_bins__'):
    // the engine's TimeOfDayBucketSpec, sent back verbatim as the group_by.
    categoryTimeBins: z
      .object({ column: z.string(), bin_minutes: z.number(), open_time: z.string() })
      .optional(),
    aggregateProfileId: z.string().nullable().optional(),
    yAxisColumn: z.string().optional(),
    groupByColumn: z.string().optional(),
    metricColumn: z.string().optional(),
    xColumn: z.string().optional(),
    yColumn: z.string().optional(),
    categoryColumn: z.string().optional(),
    aggregateFunction: z.string().optional(),
    chartTitle: z.string().optional(),
    barDirection: z.enum(['vertical', 'horizontal']).optional(),
  })
  .passthrough()

// One Alert Window panel is one window: it opens it on the matcher while
// mounted. `windowId` names a pre-panel-state RDS row still to be copied in.
export const AlertWindowsPanelStateSchema = z
  .object({
    windowId: z.string().nullable().optional(),
    name: z.string().optional(),
    alerts: z.array(AlertSpecSchema).optional(),
    symbols: z.array(z.string()).optional(),
    notify: z.boolean().optional(),
    paused: z.boolean().optional(),
    filters: z.array(z.unknown()).optional(),
    filterProfileId: z.string().nullable().optional(),
    // Columns and Format (phase 3: the fire fields; phase 4 adds scanner columns).
    selectedColumns: z.array(z.string()).optional(),
    colorRules: z.array(ColorRuleSchema).optional(),
    columnDisplayModes: ColumnDisplayModesSchema.optional(),
    // Notifications: the Scanner's audio settings, per panel. Absent: the default (sound on).
    audioSettings: audioSettingsSchema.optional(),
    // The mode the one-click mute restores. Absent: Play sound.
    audioLastMode: z.enum(['sound', 'tts']).optional(),
  })
  .passthrough()

export type AlertWindowsPanelState = z.infer<typeof AlertWindowsPanelStateSchema>

// ─── Validation Dispatcher ───

/**
 * The schema a component type's persisted state is validated against, or null
 * when the type has none and any object is accepted (GenericPanelStateSchema).
 *
 * Exported so the dashboard preset drift guard checks each preset panel against
 * the exact schema the store validates it with on load.
 */
export function panelStateSchemaFor(componentType: string): z.ZodTypeAny | null {
  switch (componentType) {
    case 'chart':
    case 'history-chart':
      return ChartPanelStateSchema
    case 'multi-chart':
      return MultiChartPanelStateSchema
    case 'scanner':
      return ScannerPanelStateSchema
    case 'multi-scanner':
      return MultiScannerPanelStateSchema
    case 'watchlist':
      return WatchlistPanelStateSchema
    case 'news':
    case 'history-news':
      return NewsPanelStateSchema
    case 'history-records':
      return HistoryRecordsPanelStateSchema
    case 'history-stats':
    case 'ticker-stats':
      return HistoryStatsPanelStateSchema
    case 'history-comparison':
      return HistoryComparisonPanelStateSchema
    case 'history-time-series':
    case 'history-scatter':
    case 'history-bar':
    case 'history-bar-category':
    case 'history-donut-category':
      return HistoryChartPanelStateSchema
    case 'notebook':
      return NotebookPanelStateSchema
    case 'history-heatmap':
      return HistoryHeatmapPanelStateSchema
    case 'history-time-distribution':
      return HistoryTimeDistributionPanelStateSchema
    case 'theme-tracker':
      return ThemeTrackerPanelStateSchema
    case 'script-editor':
      return ScriptEditorPanelStateSchema
    case 'alert-windows':
      return AlertWindowsPanelStateSchema
    default:
      return null
  }
}

/**
 * Validates raw panel state against the appropriate schema for its component type.
 * Returns the validated state or null if validation fails.
 *
 * Saved state is also repaired here, on load only: a SAR saved before the slot-order
 * fix is reordered (see remapLegacySarParams). It runs for every component type
 * because a chart grid also lives in panels with no schema of their own
 * (history-multi-chart, the backtest panels), so no chart schema can own it.
 */
export function validatePanelState(componentType: string, raw: unknown): PanelState | null {
  try {
    const schema = panelStateSchemaFor(componentType) ?? GenericPanelStateSchema
    const result = schema.safeParse(raw)

    if (!result.success) {
      console.warn(`[validatePanelState] ${componentType} validation failed:`, result.error.issues)
      return null
    }
    return remapLegacySarIndicators(result.data) as PanelState
  } catch {
    return null
  }
}
